import React from 'react';
import { Result, Button, Space, Typography, Tag } from 'antd';
import { Lock, CreditCard, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useHasAdminAccess } from '../hooks/useHasAccess';
import { ROUTES } from '../constants/routes';
import { BRAND_PRIMARY } from '../constants/brand';

interface Props {
  status: string;
  expiresAt: string | null;
}

const PRIMARY_COLOR = BRAND_PRIMARY;

// Statuses where the subscription's recorded date is a FUTURE deadline
// (the manager hasn't been cut off yet) — we render "Vence em" instead
// of the past-tense "Expirou em" so we don't lie to users on past_due /
// unpaid where they still have access.
const FUTURE_DEADLINE_STATUSES = new Set(['past_due', 'unpaid']);

/**
 * Full-page block shown to non-manager users when the tenant subscription
 * is not active. Manager-tier roles (admin/tenant_admin/diretor/gestor)
 * and account_owners bypass this via SubscriptionGate.
 *
 * Manager-tier users (rare — they normally bypass this gate via
 * SubscriptionGate, but they may land here if RBAC is misconfigured or
 * during a brief race) see an extra "Ver planos" button that takes them
 * to /admin/billing so they can re-subscribe immediately.
 */
const SubscriptionExpiredPage: React.FC<Props> = ({ status, expiresAt }) => {
  const { logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canViewBilling = useHasAdminAccess();

  const formattedExpiry = expiresAt
    ? new Date(expiresAt).toLocaleDateString('pt-BR')
    : null;

  // i18n-aware status label: looks up `billing.statusLabel.<status>` and
  // falls back to the localised `unknown` string. Replaces the previous
  // pt-BR-only constant map (which broke EN copy outright).
  const statusLabel = t(
    `billing.statusLabel.${status || 'unknown'}`,
    status ? status.charAt(0).toUpperCase() + status.slice(1) : t('billing.statusLabel.unknown', 'Indisponível'),
  );

  // Tense-aware label for the expiry date row. `past_due` and `unpaid`
  // mean the deadline is in the FUTURE (the manager still has access),
  // so we say "Vence em" — not "Expirou em".
  const dateLabelKey = FUTURE_DEADLINE_STATUSES.has(status)
    ? 'billing.expired.dueOnLabel'
    : 'billing.expired.expiredOnLabel';
  const dateLabelFallback = FUTURE_DEADLINE_STATUSES.has(status)
    ? 'Vence em'
    : 'Expirou em';

  return (
    <div
      style={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <Result
        icon={
          <div
            style={{
              width: 96,
              height: 96,
              margin: '0 auto',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${PRIMARY_COLOR}1A`,
            }}
          >
            <Lock size={44} color={PRIMARY_COLOR} strokeWidth={1.75} />
          </div>
        }
        title={
          <Typography.Title level={3} style={{ marginBottom: 8 }}>
            {t('billing.expired.title', 'Sua assinatura precisa ser renovada')}
          </Typography.Title>
        }
        subTitle={
          <div style={{ maxWidth: 520, margin: '0 auto' }}>
            <Typography.Paragraph style={{ fontSize: 15, color: '#4B5563', marginBottom: 16 }}>
              {canViewBilling
                ? t(
                    'billing.expired.subtitleManager',
                    'O acesso da equipe está pausado. Renove em poucos cliques para todos voltarem a operar.',
                  )
                : t(
                    'billing.expired.subtitleOperational',
                    'Pedimos para avisar quem cuida da conta — em poucos cliques a assinatura é renovada e o acesso volta na sequência.',
                  )}
            </Typography.Paragraph>
            <Space size={16} wrap style={{ justifyContent: 'center' }}>
              <Space size={6}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('billing.expired.statusLabel', 'Status atual')}:
                </Typography.Text>
                <Tag color="error" style={{ margin: 0, fontWeight: 500 }}>
                  {statusLabel}
                </Tag>
              </Space>
              {formattedExpiry && (
                <Space size={6}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t(dateLabelKey, dateLabelFallback)}:
                  </Typography.Text>
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    {formattedExpiry}
                  </Typography.Text>
                </Space>
              )}
            </Space>
          </div>
        }
        extra={
          <Space size={12} wrap style={{ justifyContent: 'center', marginTop: 8 }}>
            {canViewBilling && (
              <Button
                type="primary"
                size="large"
                icon={<RefreshCw size={16} />}
                onClick={() => navigate(ROUTES.ADMIN_BILLING)}
                style={{
                  background: PRIMARY_COLOR,
                  borderColor: PRIMARY_COLOR,
                  fontWeight: 500,
                }}
              >
                {t('billing.expired.renewCta', 'Renovar assinatura agora')}
              </Button>
            )}
            {canViewBilling && (
              <Button
                size="large"
                icon={<CreditCard size={16} />}
                onClick={() => navigate(ROUTES.ADMIN_BILLING)}
              >
                {t('billing.expired.viewPlansCta', 'Ver planos e cobrança')}
              </Button>
            )}
            <Button onClick={() => logout()} type="default" size="large">
              {t('billing.expired.logout', 'Sair')}
            </Button>
          </Space>
        }
      />
    </div>
  );
};

export default SubscriptionExpiredPage;
